import { type Call, useCalls } from '@hostesa/api-client';
import { useMemo } from 'react';
import { isCallActive } from '../../routes/calls/components/call-presentation.ts';
import type { PresentationResult, RecentCallTone, RecentCallVM } from './types.ts';

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.round(totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Non-color-only tone for a call status (the label always carries meaning). */
function toneFor(status: Call['status']): RecentCallTone {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'alert';
  return isCallActive(status) ? 'accent' : 'neutral';
}

/** Upper-cased language code, or undefined when absent. */
function langCode(language: unknown): string | undefined {
  return typeof language === 'string' && language.trim()
    ? language.trim().toUpperCase()
    : undefined;
}

/** Formatted mm:ss duration, or undefined when unknown. */
function durationLabel(value: unknown): string | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? formatMmSs(value) : undefined;
}

/**
 * Recent-calls ledger — the most recent ≤4 calls from the canonical `useCalls`
 * query (same query key as the desk hook, so no extra request). Every field is
 * real: language, status and duration come straight off the call. Caller
 * identity is honest — the backend carries no guest name/phone, so `name` is
 * omitted and the page fills a plain "Unknown caller" label. The raw call
 * status is passed through as `statusLabel`; the page translates it.
 */
export function useRecentCalls(
  tenantId: string,
  hotelId: string,
): PresentationResult<RecentCallVM[]> {
  const calls = useCalls(tenantId, hotelId, { limit: 50 });
  const data = useMemo<RecentCallVM[] | undefined>(() => {
    if (!calls.data) return undefined;
    return calls.data.items.slice(0, 4).map((c) => ({
      id: c.id,
      lang: langCode(c.language),
      statusLabel: c.status,
      statusTone: toneFor(c.status),
      duration: durationLabel(c.durationSeconds),
    }));
  }, [calls.data]);
  return { data, isLoading: calls.isLoading, isError: calls.isError };
}
